/** Client-side mirror of server hub cache channel ids (see src/api/hub-cache/hub-cache-channels.ts). */

function encodeRouteKeyForHubCacheChannel(routeKey: string): string {
  const bytes = new TextEncoder().encode(routeKey);
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/u, "");
}

export const HUB_CACHE_CHANNEL = {
  manufacturingReportingDailyScans: "manufacturing.reporting.daily_scans",
  manufacturingWorkOrdersList: "manufacturing.work_orders.list",
  manufacturingWirelessIssueWorkspace: "manufacturing.wireless_issue.workspace",
  manufacturingPottingSendScsOverview: "manufacturing.potting_send.scs_overview",
  manufacturingPottingSendScsDraftRows: "manufacturing.potting_send.scs_draft_rows",
  mrpTlaPlanningKodiakPcbaMatch: "mrp.tla_planning.kodiak_pcba_match",
  mrpTlaPlanningOverview: "mrp.tla_planning.overview",
  /** Forecast Resync inventory busy state (shared across MRP viewers). */
  mrpInventoryRefreshFull: "mrp.inventory.refresh_full",
  erpIngestRuns: "erp.ingest.runs",
  erpIngestControl: "erp.ingest.control",
  accountingApInvoices: "accounting.ap.invoices",
  shippingInboundReceipt: (
    sourceType: string,
    sourceId: string | number,
    arrivalPackageId: string | number | null,
  ) => `shipping.inbound.receipt:${sourceType}:${sourceId}:${arrivalPackageId ?? ""}`,
  mrpForecast: (cacheKey: string) => `mrp.forecast:${cacheKey}`,
  mrpClampForecast: (cacheKey: string) => `mrp.clamp_forecast:${cacheKey}`,
  mrpOverstock: (cacheKey: string) => `mrp.overstock:${cacheKey}`,
  mrpIncoming: (cacheKey: string) => `mrp.incoming:${cacheKey}`,
  mrpPurchaseOrders: (cacheKey: string) => `mrp.purchase_orders:${cacheKey}`,
  mrpSafetyStockAlerts: "mrp.safety_stock_alerts",
  mrpForecastSourcing: "mrp.forecast_sourcing",
  mrpPlanningOrderLines: (cacheKey: string) => `mrp.planning.order_lines_by_quarter:${cacheKey}`,
  manufacturingReportingStationWip: "manufacturing.reporting.station_wip",
  manufacturingProductionCalendar: (viewKey: string) => `manufacturing.production_calendar:${viewKey}`,
  manufacturingReportingDailyTask: (date: string) => `manufacturing.reporting.daily_task:${date}`,
  manufacturingReportingUnattributedLabor: (days: number) =>
    `manufacturing.reporting.unattributed_labor:${String(days)}`,
  manufacturingReportingProductionCost: (windowKey: string) =>
    `manufacturing.reporting.production_cost:${windowKey}`,
  manufacturingReportingProductionCostBom: (windowKey: string) =>
    `manufacturing.reporting.production_cost_bom:${windowKey}`,
  hubLaborPresence: (userId: string) => `hub.labor.presence:${userId}`,
  hubNotificationsUnread: (userId: string) => `hub.notifications.unread:${userId}`,
  hubIdentityMe: (userId: string) => `hub.identity.me:${userId}`,
  hubIdentityModules: "hub.identity.modules",
  salesOrderResource: (orderId: string | number) => `sales_orders.resource:${orderId}`,
  hubPagePresenceAll: "hub.page.presence.all",
  hubPagePresenceRoute: (routeKey: string) =>
    `hub.page.presence.route:${encodeRouteKeyForHubCacheChannel(routeKey)}`,
};

export type HubLaborPresenceReason =
  | "session_started"
  | "session_ended"
  | "idle_timeout"
  | "stale_sweep"
  | "reopened";

export type HubPagePresenceChangeReason =
  | "tab_opened"
  | "tab_closed"
  | "route_changed"
  | "state_changed";

export type HubCacheSseEvent = {
  phase: "connected" | "revalidating" | "ready" | "invalidated" | "patch";
  channel: string;
  cached_at?: string;
  stale?: boolean;
  revalidating?: boolean;
  next_refresh_after?: string;
  channels?: string[];
  labor_reason?: HubLaborPresenceReason;
  labor_session_id?: number | null;
  labor_page?: string | null;
  labor_tab_id?: string | null;
  labor_idle_timeout?: boolean;
  page_presence_reason?: HubPagePresenceChangeReason;
  page_presence_route_key?: string;
  page_presence_viewers?: Array<{
    userId: string;
    email: string;
    displayName: string | null;
    avatarUrl: string | null;
    presenceState: "active" | "idle";
    isVisible: boolean;
    tabCount: number;
    lastInteractionAt: string;
    lastHeartbeatAt: string;
  }>;
  unread_count?: number;
  surface_event_ids?: string[];
  production_calendar_batch_id?: number;
  production_calendar_batch?: Record<string, unknown> & { batchId: number };
};

export function hubCacheChannelMatches(event: HubCacheSseEvent, channel: string): boolean {
  return event.channel === channel;
}
